/**
 * ProfileForm
 *
 * Controlled form for updating profile details (first/last name). Tracks
 * modifications to enable/disable submission.
 */
import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/inputs/input';
import { Label } from '../../ui/inputs/label';
import { Check, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ProfileForm({ user, isUpdating, messages, onUpdateProfile }) {
  const [isFormModified, setIsFormModified] = useState(false);
  
  // Get initial values from user object, handling both data formats
  const getInitialFirstName = () => {
    if (user?.firstName) return user.firstName;
    if (user?.displayName) return user.displayName.split(' ')[0] || '';
    return '';
  };
  
  const getInitialLastName = () => {
    if (user?.lastName) return user.lastName;
    if (user?.displayName) {
      const nameParts = user.displayName.split(' ');
      return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
    }
    return '';
  };
  
  const [formValues, setFormValues] = useState({
    firstName: getInitialFirstName(),
    lastName: getInitialLastName()
  });

  // Reset form values when user changes
  useEffect(() => {
    setFormValues({
      firstName: getInitialFirstName(),
      lastName: getInitialLastName()
    });
    setIsFormModified(false);
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Check if any value is different from the original
    const originalFirstName = getInitialFirstName();
    const originalLastName = getInitialLastName();
    
    if (name === 'firstName') {
      setIsFormModified(value !== originalFirstName || formValues.lastName !== originalLastName);
    } else {
      setIsFormModified(formValues.firstName !== originalFirstName || value !== originalLastName);
    }
  };

  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 md:grid-cols-[300px,1fr]">
      <div className="py-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          Personal Information
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Update your personal information.
        </p>
      </div>
      <form
        onSubmit={onUpdateProfile}
        className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-2">
          <div className="sm:col-span-3">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              name="firstName"
              value={formValues.firstName}
              onChange={handleInputChange}
              className="mt-2"
            />
          </div>
          <div className="sm:col-span-3">
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              name="lastName"
              value={formValues.lastName}
              onChange={handleInputChange}
              className="mt-2"
            />
          </div>
          <div className="col-span-full">
            <div className="flex items-center justify-between">
              <Label>Email address</Label>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-gray-700 dark:text-gray-300">{user?.email}</span>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              To change your email address, please <Link to="/app/support" className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">contact support</Link>.
            </p>
          </div>
        </div>

        {messages.success && (
          <div className="mb-3 mt-4 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <Check className="h-4 w-4" />
            {messages.success}
          </div>
        )}
        {messages.error && (
          <div className="mb-3 mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <X className="h-4 w-4" />
            {messages.error}
          </div>
        )}

        <div className="mt-8 flex">
          <button
            type="submit"
            disabled={isUpdating || !isFormModified}
            className={`rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
              isFormModified 
                ? 'bg-primary-600 hover:bg-primary-700' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {isUpdating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}